import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Upload, FileUp, ListChecks } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryRetailService from '../../services/nurseryRetail';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz, formatDateInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { addDays } from '../../utils/payPeriod';
import { ManagerSeatRequired, SeatLoading } from '../../components/nursery/retail/ManagerSeatRequired';
import { XdockRecheckPanel } from '../../components/nursery/retail/XdockRecheckPanel';
import { XdockCutoffSetting } from '../../components/nursery/retail/XdockCutoffSetting';
import { XDOCK_DAY_NAMES, type XdockFilePreview, type XdockSchedule } from '../../types/nurseryRetail';

/**
 * /nursery/cross-dock — the retailer's cross dock pick lists.
 *
 * The retailer sends a sheet whenever pick days change: a standing one, and
 * one-week sheets for public holidays. Uploading reads the sheet and shows
 * what was found before anything is saved. A purchase order that asks for a
 * day its store is not picked is moved forward to the next day it is.
 *
 * docs/retailer-po-and-cross-dock-plan.md
 */

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm';

/** A file's bytes as base64, for a JSON request. */
function readBase64(file: File, unreadable: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result || '');
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error(unreadable));
    reader.readAsDataURL(file);
  });
}

interface SaveForm {
  name: string;
  is_default: boolean;
  commence_date: string;
  end_date: string;
}

function CrossDockSchedules() {
  const { t } = useTranslation('nursery', { keyPrefix: 'CrossDockSchedules' });
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [schedules, setSchedules] = useState<XdockSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<XdockFilePreview | null>(null);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [form, setForm] = useState<SaveForm | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  // Bumped whenever the schedules change, so the wrong-day list is read again.
  const [changes, setChanges] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await nurseryRetailService.getSchedules();
      if (res.success) setSchedules(res.data || []);
      else setError(errorMessage(res, t('loadFailed')));
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (canManage) load();
  }, [canManage, load]);

  const formFor = (p: XdockFilePreview, idx: number): SaveForm => {
    const meta = p.sheets[idx].meta;
    return {
      name: meta.name || p.file_name.replace(/\.[^.]+$/, ''),
      // A sheet that says when it commences is for that week. One that does
      // not is the standing schedule.
      is_default: !meta.commence_date,
      commence_date: meta.commence_date ?? '',
      end_date: meta.commence_date ? addDays(meta.commence_date, 6) : '',
    };
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await nurseryRetailService.previewSchedule(file.name, await readBase64(file, t('fileUnreadable')));
      if (res.success && res.data) {
        setPreview(res.data);
        setSheetIdx(0);
        setForm(formFor(res.data, 0));
      } else setError(errorMessage(res, t('readFailed')));
    } catch (e) {
      setError(errorMessage(e, t('readFailed')));
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const chooseSheet = (idx: number) => {
    if (!preview) return;
    setSheetIdx(idx);
    setForm(formFor(preview, idx));
  };

  const cancelPreview = () => {
    setPreview(null);
    setForm(null);
  };

  const save = async () => {
    if (!preview || !form) return;
    const sheet = preview.sheets[sheetIdx];
    if (!form.is_default && !form.commence_date) {
      setError(t('needFirstDay'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryRetailService.saveSchedule({
        name: form.name.trim() || t('defaultName'),
        is_default: form.is_default,
        commence_date: form.is_default ? null : form.commence_date,
        end_date: form.is_default ? null : (form.end_date || null),
        updated_note: sheet.meta.updated_note || null,
        file_name: preview.file_name,
        sheet_name: sheet.sheet_name,
        rows: sheet.rows,
      });
      if (res.success) {
        cancelPreview();
        setMessage(t('saved', { name: res.data.name }));
        setChanges((n) => n + 1);
        await load();
      } else setError(errorMessage(res, t('saveFailed')));
    } catch (e) {
      setError(errorMessage(e, t('saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await fn();
      setMessage(done);
      setRemoving(null);
      setChanges((n) => n + 1);
      await load();
    } catch (e) {
      setError(errorMessage(e, t('actFailed')));
    } finally {
      setBusy(false);
    }
  };

  if (licenseLoading) return <SeatLoading />;
  if (!canManage) return <ManagerSeatRequired what={t('seatWhat')} />;

  const sheet = preview ? preview.sheets[sheetIdx] : null;

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<CalendarClock className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/nursery/orders/import')}>
              <FileUp className="h-4 w-4" /> {t('importPos')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/nursery/retailer-range')}>
              <ListChecks className="h-4 w-4" /> {t('rangeUpload')}
            </Button>
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
        {message && <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800 break-words">{message}</div>}

        <XdockRecheckPanel refreshKey={changes} />

        {!sheet || !form ? (
          <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
            <h2 className="text-sm font-semibold text-neutral-800">{t('uploadTitle')}</h2>
            <p className="text-sm text-neutral-600">
              {t('uploadHint')}
            </p>
            <input
              ref={fileInput}
              id="xdock-file"
              type="file"
              accept=".xlsx,.xlsm,.csv"
              className="sr-only"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
            <Button size="sm" disabled={busy} onClick={() => fileInput.current?.click()}>
              <Upload className="h-4 w-4" /> {busy ? t('reading') : t('chooseFile')}
            </Button>
          </section>
        ) : (
          <section aria-label={t('previewLabel')} className="bg-white border border-primary-200 rounded-xl p-4 space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-neutral-800">{t('previewTitle')}</h2>
              <p className="text-xs text-neutral-500 break-words">{preview!.file_name}</p>
            </div>

            {preview!.sheets.length > 1 && (
              <div>
                <label htmlFor="xdock-sheet" className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('whichSheet', { count: preview!.sheets.length })}
                </label>
                <select id="xdock-sheet" className={inputCls} value={sheetIdx} onChange={(e) => chooseSheet(Number(e.target.value))}>
                  {preview!.sheets.map((s, i) => (
                    <option key={i} value={i}>{s.sheet_name || s.meta.name || t('sheetN', { n: i + 1 })}</option>
                  ))}
                </select>
              </div>
            )}

            <dl className="grid grid-cols-3 gap-2 text-center">
              {[
                [t('stat.rows'), sheet.summary.rows],
                [t('stat.stores'), sheet.summary.stores],
                [t('stat.pickDays'), sheet.meta.day_columns],
              ].map(([label, value]) => (
                <div key={label} className="bg-neutral-50 rounded-lg px-2 py-2">
                  <dd className="text-lg font-semibold text-neutral-900">{value}</dd>
                  <dt className="text-xs text-neutral-500">{label}</dt>
                </div>
              ))}
            </dl>

            <ul className="text-sm divide-y divide-neutral-100 border border-neutral-200 rounded-lg">
              {XDOCK_DAY_NAMES.map((day, d) => (
                <li key={day} className="flex justify-between gap-3 px-3 py-1.5">
                  <span className="text-neutral-800">{t(`day.${day}`)}</span>
                  <span className={sheet.summary.per_day[d] ? 'text-neutral-800' : 'text-neutral-400'}>
                    {sheet.summary.per_day[d] ? t('dayStores', { count: sheet.summary.per_day[d] }) : t('nothingPicked')}
                  </span>
                </li>
              ))}
            </ul>

            <div className="text-sm text-neutral-700">
              <p>
                {t('matched', { matched: sheet.stores.matched, stores: sheet.summary.stores })}
                {sheet.stores.unmatched.length > 0 && t('restNeedNothing')}
              </p>
              {sheet.stores.unmatched.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-primary-700">
                    {t('unmatched', { count: sheet.stores.unmatched.length })}
                  </summary>
                  <p className="mt-1 text-xs text-neutral-500">
                    {t('unmatchedHint')}
                  </p>
                  <ul className="mt-1 max-h-48 overflow-y-auto text-xs text-neutral-600 space-y-0.5">
                    {sheet.stores.unmatched.map((s) => (
                      <li key={s.store_code} className="break-words">{s.store_code} {s.store_name}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label htmlFor="xdock-name" className="block text-sm font-medium text-neutral-700 mb-1">{t('nameLabel')}</label>
                <input id="xdock-name" className={inputCls} maxLength={150} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>

              <label className="flex items-start gap-2 text-sm text-neutral-800">
                <input type="checkbox" className="mt-0.5 h-4 w-4 flex-shrink-0" checked={form.is_default}
                  onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
                <span>
                  {t('standingLabel')}
                  <span className="block text-xs text-neutral-500">
                    {t('standingHint')}
                  </span>
                </span>
              </label>

              {!form.is_default && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="xdock-from" className="block text-sm font-medium text-neutral-700 mb-1">{t('firstDay')}</label>
                    <input id="xdock-from" type="date" className={inputCls} value={form.commence_date}
                      onChange={(e) => setForm({
                        ...form,
                        commence_date: e.target.value,
                        end_date: e.target.value ? addDays(e.target.value, 6) : form.end_date,
                      })} />
                  </div>
                  <div>
                    <label htmlFor="xdock-to" className="block text-sm font-medium text-neutral-700 mb-1">{t('lastDay')}</label>
                    <input id="xdock-to" type="date" className={inputCls} value={form.end_date}
                      onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                    <p className="text-xs text-neutral-500 mt-1">{t('lastDayHint')}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={save} disabled={busy}>{busy ? t('saving') : t('save')}</Button>
              <Button size="sm" variant="outline" onClick={cancelPreview} disabled={busy}>{t('cancel')}</Button>
            </div>
          </section>
        )}

        <XdockCutoffSetting />

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-neutral-800">{t('inUse')}</h2>
          {loading ? (
            <div className="h-32 bg-neutral-100 rounded-xl animate-pulse" />
          ) : schedules.length === 0 ? (
            <div className="bg-white border border-neutral-200 rounded-xl p-6 text-sm text-neutral-600">
              {t('empty')}
            </div>
          ) : (
            <ul className="space-y-3">
              {schedules.map((s) => (
                <li key={s.id} className="bg-white border border-neutral-200 rounded-xl p-4">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <Link to={`/nursery/cross-dock/${s.id}`} className="font-semibold text-primary-700 hover:underline break-words min-w-0">
                      {s.name}
                    </Link>
                    {s.is_default && (
                      <span className="rounded-full bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5">{t('standingBadge')}</span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-neutral-700 break-words">
                    {s.is_default || !s.commence_date
                      ? t('anyDate')
                      : t('dateRange', { from: formatLocalDateInTz(s.commence_date, tz), to: s.end_date ? formatLocalDateInTz(s.end_date, tz) : '' })}
                  </p>
                  <p className="mt-0.5 text-xs text-neutral-500 break-words">
                    {t('uploaded', { count: s.store_count, date: formatDateInTz(s.created_at, tz) })}
                    {s.created_by_name ? t('by', { name: s.created_by_name }) : ''}
                    {s.updated_note ? t('sheetNote', { note: s.updated_note }) : ''}
                  </p>
                  {removing === s.id ? (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 space-y-2">
                      <p>
                        {t('removeConfirm', { name: s.name })}{' '}
                        {s.is_default
                          ? t('removeStanding')
                          : t('removeDated')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="danger" disabled={busy}
                          onClick={() => act(() => nurseryRetailService.removeSchedule(s.id), t('removed', { name: s.name }))}>
                          {t('remove')}
                        </Button>
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => setRemoving(null)}>{t('keep')}</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/nursery/cross-dock/${s.id}`)}>{t('storesAndTester')}</Button>
                      {!s.is_default && (
                        <Button size="sm" variant="outline" disabled={busy}
                          onClick={() => act(() => nurseryRetailService.makeDefault(s.id), t('nowStanding', { name: s.name }))}>
                          {t('makeStanding')}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => setRemoving(s.id)}>{t('remove')}</Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <p className="text-xs text-neutral-500">
          {t('footnote')}
        </p>
      </div>
    </>
  );
}

export default CrossDockSchedules;
